import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';

interface Screenshot {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  placeholder?: string;
  badge?: string;
  storyName?: string; // For auto-generated screenshots
}

interface IntegrationScreenshotsProps {
  screenshots: Screenshot[];
  title?: string;
  description?: string;
}

export const IntegrationScreenshots: React.FC<IntegrationScreenshotsProps> = ({
  screenshots,
  title,
  description
}) => {
  const { t } = useTranslation();

  const defaultTitle = title || t('screenshots.title', { ns: 'homepage', defaultValue: 'See It In Action' });
  const defaultDescription = description || t('screenshots.description', { ns: 'homepage', defaultValue: 'Visual walkthrough of the integration setup and usage' });

  return (
    <section className="bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{defaultTitle}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{defaultDescription}</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {screenshots.map((screenshot) => (
            <Card key={screenshot.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="aspect-video bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center relative">
                {screenshot.imageUrl || screenshot.storyName ? (
                  <img 
                    src={screenshot.imageUrl || `/marketing-assets/${screenshot.storyName}.png`} 
                    alt={screenshot.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback to placeholder if image fails to load
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'block';
                    }}
                  />
                ) : null}
                <div 
                  className="text-center p-6"
                  style={{ display: screenshot.imageUrl || screenshot.storyName ? 'none' : 'block' }}
                >
                  <div className="w-16 h-16 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">📱</span>
                  </div>
                  <div className="text-sm text-gray-500 font-mono">
                    {screenshot.placeholder || 'Screenshot Coming Soon'}
                  </div>
                </div>
                {screenshot.badge && (
                  <Badge className="absolute top-3 right-3 bg-indigo-600 text-white">
                    {screenshot.badge}
                  </Badge>
                )}
              </div>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{screenshot.title}</h3>
                <p className="text-gray-600 text-sm">{screenshot.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            {t('screenshots.storybook_note', { 
              ns: 'homepage', 
              defaultValue: '💡 Screenshots generated from our interactive Storybook component library' 
            })}
          </p>
        </div>
      </div>
    </section>
  );
};

export default IntegrationScreenshots;
